import { CRUDMutations } from '@/utils/store-utilities'
// import axios from '@nuxtjs/axios'

const PRODUCT_FIT_SCORE_KEY = 'product_fit_score'

const normalizeMetaValue = (value) => {
  if (value === undefined || value === null) {
    return ''
  }

  return `${value}`
}

const normalizeProductFitScoreValue = (value) => {
  if (value === undefined || value === null || value === '') {
    return null
  }

  const normalizedValue = `${value}`.trim()
  if (!/^(10|[0-9])$/.test(normalizedValue)) {
    throw new Error('Product fit score must be an integer between 0 and 10.')
  }

  return normalizedValue
}

const getMetaMapKey = (meta) => {
  if (meta?.meta_key_id) {
    return `id:${meta.meta_key_id}`
  }

  if (meta?.metaKey?.key) {
    return `key:${meta.metaKey.key}`
  }

  return null
}

const mergeMetas = (baseMetas = [], editedMetas = []) => {
  const mergedMetasMap = new Map()

  baseMetas.forEach((meta) => {
    const key = getMetaMapKey(meta)
    if (key) {
      mergedMetasMap.set(key, {
        ...meta,
      })
    }
  })

  editedMetas.forEach((meta) => {
    const key = getMetaMapKey(meta)
    if (key) {
      mergedMetasMap.set(key, {
        ...(mergedMetasMap.get(key) || {}),
        ...meta,
      })
    }
  })

  return Array.from(mergedMetasMap.values())
}

const isProductFitScoreMeta = (meta) => {
  return meta?.metaKey?.key === PRODUCT_FIT_SCORE_KEY
}

const upsertProductFitScoreMeta = (metas = []) => {
  const productFitScoreIndex = metas.findIndex((meta) => {
    return isProductFitScoreMeta(meta)
  })

  if (productFitScoreIndex === -1) {
    return metas
  }

  const fitScoreMeta = metas[productFitScoreIndex]
  const fitScoreValue = normalizeProductFitScoreValue(fitScoreMeta?.value)

  if (fitScoreValue === null) {
    return metas.filter((_, index) => index !== productFitScoreIndex)
  }

  metas[productFitScoreIndex] = {
    ...fitScoreMeta,
    value: fitScoreValue,
  }

  return metas
}

const normalizeMetasForPayload = (baseMetas = [], editedMetas = []) => {
  const baseHasFitScore = baseMetas.some(isProductFitScoreMeta)
  const editedHasFitScore = editedMetas.some(isProductFitScoreMeta)

  const mergedMetas = mergeMetas(baseMetas, editedMetas)

  if (baseHasFitScore && !editedHasFitScore) {
    return mergedMetas
      .filter((meta) => !isProductFitScoreMeta(meta))
      .map((meta) => ({
        ...meta,
        value: normalizeMetaValue(meta.value),
      }))
  }

  const metasWithUpsertedFitScore = upsertProductFitScoreMeta(mergedMetas)

  return metasWithUpsertedFitScore.map((meta) => {
    return {
      ...meta,
      value: normalizeMetaValue(meta.value),
    }
  })
}

const defaultItem = (item) => ({
  title: '',
  description: '',
  relatedProducts: [],

  // SEO
  slug: '',
  tags: [],

  // Images
  media_id: null, // Should be pickd
  second_media_id: null, // from ImageLoader.
  gallery: [],

  // Inventory
  sku: '',
  barcode: '',
  quantity: 0, // make { location: quantity } kv pair object.
  track_quantity: false,
  sell_out_of_stock: false,

  // Locations
  locations: [],
  location_id: '',

  // Pricing
  price: 0, // Valid
  charge_tax: false,

  // Variants
  has_variants: false,
  productVariants: [],

  // Shipping
  is_physical: true,
  weight: 0,
  hs_code: '',

  // Organization
  product_type_id: null,
  product_label_id: null,
  vendor_id: null,
  collections: [],
  is_affiliate_eligible: false,
  is_listed_amazon: false,
  is_listed_everyday_deals_ebay: false,
  is_listed_superdeals_ebay: false,
  is_listed_yabba_ebay: false,
  status: 0,
  ...item,
})

// Product relation fields the backend validates as integer IDs (BaseRequest `*.* => int`).
// The edit form binds these to DcSelect, which converts loaded objects to IDs on mount; but
// when a DcSelect remount skips that conversion the raw objects survive in state and the save
// is rejected (e.g. "addOnCollections.0 must be an integer"). Normalising at the API boundary
// keeps the payload aligned with the contract regardless of the UI timing.
const RELATION_ID_LIST_FIELDS = [
  'tags',
  'relatedProducts',
  'manualCollections',
  'customCategories',
  'addOnProducts',
  'addOnCollections',
]

const toRelationId = (value) => (value && typeof value === 'object' ? value.id : value)

// Map every relation field to its integer ID. Idempotent: existing IDs pass through unchanged.
const normalizeRelationIds = (payload) => {
  const normalized = { ...payload }

  RELATION_ID_LIST_FIELDS.forEach((field) => {
    if (Array.isArray(normalized[field])) {
      normalized[field] = normalized[field].map(toRelationId)
    }
  })

  if (normalized.new_version_product_id && typeof normalized.new_version_product_id === 'object') {
    normalized.new_version_product_id = normalized.new_version_product_id.id
  }

  return normalized
}

export const state = () => ({
  items: [],
})

export const mutations = {
  ...CRUDMutations(defaultItem),
}

export const getters = {
  list(state) {
    return state.items
  },
  reversed(state) {
    return [...state.items].reverse()
  },
}

export const actions = {
  async getProduct({ commit }, id) {
    const item = await this.$axios.$get(`admin/products/${id}`)

    commit('set', item.data)
  },
  async createProduct({ commit }, collection) {
    const metas = normalizeMetasForPayload(collection.metas, collection.metas)
    const created = await this.$axios.$post(
      'admin/products/create',
      normalizeRelationIds({
        ...defaultItem(collection),
        metas,
      })
    )

    commit('add', defaultItem(created.data))
    return created
  },
  async updateProduct({ commit }, collection) {
    const { editedMetas, ...productPayload } = collection
    const baseMetas = Array.isArray(productPayload.metas) ? productPayload.metas : []
    const normalizedEditedMetas = Array.isArray(editedMetas) ? editedMetas : baseMetas
    const metas = normalizeMetasForPayload(baseMetas, normalizedEditedMetas)
    const update = await this.$axios.$put(
      `admin/products/${productPayload.id}`,
      normalizeRelationIds({
        ...defaultItem(productPayload),
        metas,
      })
    )

    commit('update', defaultItem(update.data))
    return update
  },
  async removeProduct({ commit }, collection) {
    const remove = await this.$axios.$delete(`admin/products/${collection.id}`)

    if (remove.data) {
      commit('remove', collection)
    }
    return remove
  },
  async fetchProducts({ commit }, params = {}) {
    const list = await this.$axios.$get('admin/products', { params })
    if (list && list.data && list.data.length) {
      commit('load', list.data.map(defaultItem))
      return
    }

    commit('clear')
  },
  fetchProduct({ commit }, id) {
    return this.$axios.$get(`admin/products/${id}`).then((response) => {
      return response.data
    })
  },
}
